import {
  ArrowLeftIcon,
  ChartBarIcon,
  ChevronDownIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';

import {
  EvaluationItem,
  getEvaluationBatch,
} from '../../services/evaluationService';
import { Card } from '../ui/card';
import { Badge } from '../ui/catalyst/badge';
import { Button } from '../ui/catalyst/button';
import { Heading } from '../ui/catalyst/heading';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/catalyst/table';
import { Text } from '../ui/catalyst/text';

interface EvaluationDetailsProps {
  batchId: string;
  onBack: () => void;
}

export const EvaluationDetails = ({
  batchId,
  onBack,
}: EvaluationDetailsProps) => {
  const [loading, setLoading] = useState(true);
  const [batch, setBatch] = useState<any>(null);
  const [items, setItems] = useState<EvaluationItem[]>([]);
  const [summaryStats, setSummaryStats] = useState<any>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [autoRefresh, setAutoRefresh] = useState(false);

  const toNumber = (value: number | null | undefined): number => {
    if (typeof value === 'number' && !Number.isNaN(value)) return value;
    return 0;
  };

  const formatOneDecimal = (value: number | null | undefined): string => {
    return toNumber(value).toFixed(1);
  };

  const fetchDetails = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getEvaluationBatch(batchId);
      setBatch(data.batch);
      setItems(data.items);
      setSummaryStats(data.summary_stats);
    } catch (error) {
      console.error('Error fetching evaluation details:', error);
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  // Initial load on mount / batchId change
  useEffect(() => {
    fetchDetails();
    // no explicit return to satisfy consistent-return
  }, [fetchDetails]);

  // Poll only when running, autoRefresh is enabled, AND no rows are expanded (avoid collapsing)
  useEffect(() => {
    if (batch?.status !== 'running' || expandedItems.size > 0 || !autoRefresh) {
      return undefined;
    }
    const interval = setInterval(() => {
      fetchDetails();
    }, 3000);
    return () => clearInterval(interval);
  }, [batch?.status, expandedItems.size, autoRefresh, fetchDetails]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'zinc';
      case 'generating':
        return 'blue';
      case 'grading':
        return 'purple';
      case 'completed':
        return 'green';
      case 'failed':
        return 'red';
      default:
        return 'zinc';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 90) return 'text-green-600';
    if (score >= 80) return 'text-blue-600';
    if (score >= 70) return 'text-yellow-600';
    if (score >= 60) return 'text-orange-600';
    return 'text-red-600';
  };

  const toggleItemExpansion = (itemId: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const renderExpandedDetails = (item: EvaluationItem) => {
    if (!item.grading_result) {
      return (
        <div className="px-4 py-3 bg-gray-50 text-sm text-gray-600">
          No detailed grading information available.
        </div>
      );
    }

    const gradingResult = item.grading_result;

    return (
      <div className="px-4 py-6 bg-gray-50 border-t">
        <div className="space-y-6">
          {/* Summary */}
          {gradingResult.summary && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">
                Overall Summary
              </h4>
              <p className="text-sm text-gray-700">{gradingResult.summary}</p>
              <div className="mt-2 flex items-center gap-4">
                <span className="text-sm font-medium">
                  Grade:{' '}
                  <span
                    className={`${getScoreColor(gradingResult.final_adjusted_score || 0)}`}
                  >
                    {gradingResult.grade}
                  </span>
                </span>
                <span className="text-sm font-medium">
                  Final Score:{' '}
                  <span
                    className={`${getScoreColor(gradingResult.final_adjusted_score || 0)}`}
                  >
                    {gradingResult.final_adjusted_score?.toFixed(1) || 0}%
                  </span>
                </span>
              </div>
            </div>
          )}

          {/* Section Breakdown */}
          {gradingResult.sections && gradingResult.sections.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Section Breakdown
              </h4>
              <div className="space-y-3">
                {gradingResult.sections.map((section: any, index: number) => (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-3 bg-white"
                  >
                    <div className="mb-2">
                      <h5 className="font-medium text-gray-900">
                        {section.name}
                      </h5>
                      <div className="mt-1 text-sm text-gray-700 space-x-3">
                        <span className="inline-block">
                          <span className="text-gray-500">Points:</span>{' '}
                          {section.score}/{section.max_points}
                        </span>
                        <span className="inline-block">
                          <span className="text-gray-500">Weight:</span>{' '}
                          {section.weight_pct}%
                        </span>
                        <span
                          className={`inline-block font-semibold ${getScoreColor((section.score / section.max_points) * 100)}`}
                        >
                          <span className="font-normal text-gray-500">
                            Weighted contribution:
                          </span>{' '}
                          {(
                            (section.score / section.max_points) *
                              section.weight_pct || 0
                          ).toFixed(2)}
                          %
                        </span>
                      </div>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">
                      {section.rationale}
                    </p>

                    {section.issues && section.issues.length > 0 && (
                      <div className="mt-2">
                        <span className="text-xs font-medium text-red-700">
                          Issues:
                        </span>
                        <ul className="list-disc list-inside ml-2 text-xs text-red-600">
                          {section.issues.map((issue: string, i: number) => (
                            <li key={i}>{issue}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {section.improvements &&
                      section.improvements.length > 0 && (
                        <div className="mt-2">
                          <span className="text-xs font-medium text-blue-700">
                            Improvements:
                          </span>
                          <ul className="list-disc list-inside ml-2 text-xs text-blue-600">
                            {section.improvements.map(
                              (improvement: string, i: number) => (
                                <li key={i}>{improvement}</li>
                              ),
                            )}
                          </ul>
                        </div>
                      )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Penalties */}
          {gradingResult.penalties && gradingResult.penalties.length > 0 && (
            <div>
              <h4 className="font-semibold text-gray-900 mb-3">
                Penalties Applied
              </h4>
              <div className="space-y-2">
                {gradingResult.penalties.map((penalty: any, index: number) => (
                  <div
                    key={index}
                    className="border-l-4 border-red-400 pl-3 py-2 bg-red-50"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-red-800">
                        {penalty.severity}: {penalty.issue}
                      </span>
                      <span className="text-sm font-semibold text-red-700">
                        {penalty.deduction} pts
                      </span>
                    </div>
                    <p className="text-sm text-red-700">{penalty.evidence}</p>
                    {penalty.remediation && (
                      <p className="text-sm text-red-600 mt-1">
                        <span className="font-medium">Fix:</span>{' '}
                        {penalty.remediation}
                      </p>
                    )}
                  </div>
                ))}
                <div className="mt-2 text-right">
                  <span className="text-sm font-medium text-red-700">
                    Total Penalties: -{gradingResult.penalties_total ?? 0}%
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const perSectionAverages = useMemo(() => {
    if (summaryStats?.per_section_averages)
      return summaryStats.per_section_averages;
    // Fallback: compute from items if not provided by backend
    const totals: Record<
      string,
      {
        score: number;
        max: number;
        weight: number;
        weighted: number;
        count: number;
      }
    > = {};
    items.forEach((item) => {
      const sections = (item as any).grading_result?.sections || [];
      sections.forEach((s: any) => {
        const name = s?.name;
        if (!name) return;
        const score = Number(s?.score || 0);
        const max = Number(s?.max_points || 0);
        const weight = Number(s?.weight_pct || 0);
        if (!totals[name])
          totals[name] = { score: 0, max: 0, weight: 0, weighted: 0, count: 0 };
        totals[name].score += score;
        totals[name].max += max;
        totals[name].weight += weight;
        totals[name].weighted += max > 0 ? (score / max) * weight : 0;
        totals[name].count += 1;
      });
    });
    const result: Record<string, any> = {};
    Object.entries(totals).forEach(([name, t]) => {
      const c = Math.max(1, t.count);
      const avgScore = t.score / c;
      const avgMax = t.max / c;
      const avgWeight = t.weight / c;
      const avgWeighted = t.weighted / c;
      const avgPercent = avgMax > 0 ? (avgScore / avgMax) * 100 : 0;
      result[name] = {
        avg_points: Number(avgScore.toFixed(2)),
        avg_max_points: Number(avgMax.toFixed(2)),
        avg_percent: Number(avgPercent.toFixed(2)),
        avg_weight_pct: Number(avgWeight.toFixed(2)),
        avg_weighted_contribution_pct: Number(avgWeighted.toFixed(2)),
      };
    });
    return result;
  }, [summaryStats, items]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="text-center py-12">
        <Text className="text-red-600">Failed to load evaluation details</Text>
        <Button onClick={onBack} className="mt-4">
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button plain onClick={onBack}>
          <ArrowLeftIcon className="h-4 w-4 mr-1" />
          Back
        </Button>
        <div className="flex-1">
          <Heading level={2}>{batch.name}</Heading>
          {batch.description && (
            <Text className="text-gray-600 mt-1">{batch.description}</Text>
          )}
        </div>
        <div className="flex items-center gap-3">
          <label
            htmlFor="auto-refresh-toggle"
            className="flex items-center gap-2 text-sm text-gray-600"
          >
            <input
              id="auto-refresh-toggle"
              type="checkbox"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh
          </label>
          <Button outline onClick={() => fetchDetails()} className="text-sm">
            Refresh
          </Button>
          <Badge color={getStatusColor(batch.status)}>{batch.status}</Badge>
        </div>
      </div>

      {/* Summary Stats */}
      {summaryStats && (
        <>
          <div className="grid grid-cols-4 gap-4">
            <Card className="p-4">
              <Text className="text-sm text-gray-600">Avg Weighted Score</Text>
              <Text
                className={`text-2xl font-semibold ${getScoreColor(
                  toNumber(
                    summaryStats.average_weighted_score ??
                      summaryStats.average_rubric_score,
                  ),
                )}`}
              >
                {formatOneDecimal(
                  summaryStats.average_weighted_score ??
                    summaryStats.average_rubric_score,
                )}
                %
              </Text>
            </Card>

            <Card className="p-4">
              <Text className="text-sm text-gray-600">
                Avg Gold Standard Similarity
              </Text>
              <Text
                className={`text-2xl font-semibold ${getScoreColor(
                  toNumber(summaryStats.average_gold_standard_score),
                )}`}
              >
                {formatOneDecimal(summaryStats.average_gold_standard_score)}%
              </Text>
            </Card>

            <Card className="p-4">
              <Text className="text-sm text-gray-600">Avg Final Score</Text>
              <Text
                className={`text-2xl font-semibold ${getScoreColor(
                  toNumber(summaryStats.average_final_score),
                )}`}
              >
                {formatOneDecimal(summaryStats.average_final_score)}%
              </Text>
            </Card>

            <Card className="p-4">
              <Text className="text-sm text-gray-600">Grading Time</Text>
              <Text className="text-2xl font-semibold">
                {Math.round(toNumber(summaryStats.total_grading_time) / 60)}m
              </Text>
            </Card>
          </div>

          {/* Per-Section Averages */}
          {perSectionAverages && Object.keys(perSectionAverages).length > 0 && (
            <Card className="p-4">
              <Heading level={4}>Per-Section Averages</Heading>
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {Object.entries(perSectionAverages).map(
                  ([name, stats]: any) => (
                    <div key={name} className="border rounded p-3 bg-white">
                      <div className="font-medium text-gray-900">{name}</div>
                      <div className="mt-1 text-sm text-gray-700">
                        <div>
                          <span className="text-gray-500">Avg points:</span>{' '}
                          {stats.avg_points}/{stats.avg_max_points}
                        </div>
                        <div>
                          <span className="text-gray-500">Avg percent:</span>{' '}
                          {stats.avg_percent}%
                        </div>
                        <div>
                          <span className="text-gray-500">Avg weight:</span>{' '}
                          {stats.avg_weight_pct}%
                        </div>
                        <div>
                          <span className="text-gray-500">
                            Avg weighted contribution:
                          </span>{' '}
                          {stats.avg_weighted_contribution_pct}%
                        </div>
                      </div>
                    </div>
                  ),
                )}
              </div>
            </Card>
          )}
        </>
      )}

      {/* Score Distribution Chart (Placeholder) */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <Heading level={3}>Score Distribution</Heading>
          <ChartBarIcon className="h-5 w-5 text-gray-400" />
        </div>

        <div className="flex items-end gap-2 h-32">
          {[90, 80, 70, 60, 50].map((threshold, idx) => {
            const count = items.filter(
              (i) =>
                i.final_score &&
                i.final_score >= threshold &&
                (idx === 0 || i.final_score < [100, 90, 80, 70, 60][idx]),
            ).length;
            const itemsLength = items.length;
            const height = itemsLength > 0 ? (count / itemsLength) * 100 : 0;

            return (
              <div
                key={threshold}
                className="flex-1 flex flex-col items-center"
              >
                <div
                  className="w-full bg-blue-500 rounded-t"
                  style={{ height: `${height}%` }}
                />
                <Text className="text-xs mt-1">{threshold}+</Text>
                <Text className="text-xs text-gray-500">{count}</Text>
              </div>
            );
          })}
        </div>
      </Card>

      {/* Results Table */}
      <Card>
        <div className="flex items-center justify-between p-4 border-b">
          <Heading level={3}>Evaluation Results</Heading>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                const completedItems = items.filter(
                  (item) => item.status === 'completed' && item.grading_result,
                );
                if (expandedItems.size === completedItems.length) {
                  setExpandedItems(new Set());
                } else {
                  setExpandedItems(
                    new Set(completedItems.map((item) => item.id)),
                  );
                }
              }}
              className="text-sm text-blue-600 hover:text-blue-800 transition-colors"
            >
              {expandedItems.size ===
              items.filter(
                (item) => item.status === 'completed' && item.grading_result,
              ).length
                ? 'Collapse All'
                : 'Expand All'}
            </button>
          </div>
        </div>
        <Table>
          <TableHead>
            <TableRow>
              <TableHeader>Matter</TableHeader>
              <TableHeader>Status</TableHeader>
              <TableHeader>Weighted Score</TableHeader>
              <TableHeader>Gold Std. Similarity</TableHeader>
              <TableHeader>Final Score</TableHeader>
              <TableHeader>Grading Time</TableHeader>
              <TableHeader>Details</TableHeader>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <Fragment key={item.id}>
                <TableRow>
                  <TableCell>
                    <Text className="font-medium">{item.matter_name}</Text>
                    {item.error_message && (
                      <Text className="text-xs text-red-600 mt-1">
                        {item.error_message}
                      </Text>
                    )}
                  </TableCell>

                  <TableCell>
                    <Badge color={getStatusColor(item.status)}>
                      {item.status}
                    </Badge>
                  </TableCell>

                  <TableCell>
                    {typeof (item.weighted_score ?? item.rubric_score) ===
                    'number' ? (
                      <Text
                        className={getScoreColor(
                          toNumber(item.weighted_score ?? item.rubric_score),
                        )}
                      >
                        {formatOneDecimal(
                          item.weighted_score ?? item.rubric_score,
                        )}
                        %
                      </Text>
                    ) : (
                      <Text className="text-gray-400">-</Text>
                    )}
                  </TableCell>

                  <TableCell>
                    {item.gold_standard_score !== null &&
                    item.gold_standard_score !== undefined ? (
                      <Text className={getScoreColor(item.gold_standard_score)}>
                        {item.gold_standard_score.toFixed(1)}%
                      </Text>
                    ) : (
                      <Text className="text-gray-400">-</Text>
                    )}
                  </TableCell>

                  <TableCell>
                    {item.final_score !== null &&
                    item.final_score !== undefined ? (
                      <Text
                        className={`font-semibold ${getScoreColor(item.final_score)}`}
                      >
                        {item.final_score.toFixed(1)}%
                      </Text>
                    ) : (
                      <Text className="text-gray-400">-</Text>
                    )}
                  </TableCell>

                  <TableCell>
                    {item.grading_time_seconds ? (
                      <Text className="text-sm">
                        {Math.round(item.grading_time_seconds)}s
                      </Text>
                    ) : (
                      <Text className="text-gray-400">-</Text>
                    )}
                  </TableCell>

                  <TableCell>
                    {item.grading_result && item.status === 'completed' ? (
                      <button
                        type="button"
                        onClick={() => toggleItemExpansion(item.id)}
                        className="flex items-center gap-1 text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        {expandedItems.has(item.id) ? (
                          <ChevronDownIcon className="h-4 w-4" />
                        ) : (
                          <ChevronRightIcon className="h-4 w-4" />
                        )}
                        <Text className="text-sm">
                          {expandedItems.has(item.id) ? 'Hide' : 'Show'} Details
                        </Text>
                      </button>
                    ) : (
                      <Text className="text-gray-400 text-sm">-</Text>
                    )}
                  </TableCell>
                </TableRow>

                {expandedItems.has(item.id) && (
                  <TableRow key={`${item.id}-expanded`}>
                    <td colSpan={7} className="p-0">
                      {renderExpandedDetails(item)}
                    </td>
                  </TableRow>
                )}
              </Fragment>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <Button outline>Export Results</Button>
      </div>
    </div>
  );
};
